import {useState} from 'react';
import type {FormEvent} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {useTranslation} from 'react-i18next';

import {Button} from 'UI/components/action/Button';
import {PasswordInput} from 'UI/components/action/PasswordInput';

const MIN_PASSWORD_LENGTH = 8;
const HTTP_STATUS_UNAUTHORIZED = 401;

/**
 * Passwort-ändern-Sektion für eingeloggte User. Verifiziert das aktuelle
 * Passwort serverseitig und setzt anschließend das neue. Authentifizierung
 * läuft über das Payload-Cookie (credentials: 'include').
 *
 * @returns ReactNode der Passwort-ändern-Sektion.
 */
export const ChangePasswordSection = () => {
    const {t} = useTranslation();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState<
        'error' | 'idle' | 'mismatch' | 'submitting' | 'success' | 'tooShort' | 'wrongCurrent'
    >('idle');

    /**
     * Validiert die Eingaben und sendet die Passwortänderung an die API.
     *
     * @param event Das Submit-Event des Formulars.
     */
    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        if (status === 'submitting') return;

        if (!currentPassword || !newPassword) {
            setStatus('error');

            return;
        }

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setStatus('tooShort');

            return;
        }

        if (newPassword !== confirmPassword) {
            setStatus('mismatch');

            return;
        }

        setStatus('submitting');

        try {
            const res = await fetch('/api/auth/change-password', {
                body: JSON.stringify({
                    currentPassword,
                    newPassword
                }),
                credentials: 'include',
                headers: {'Content-Type': 'application/json'},
                method: 'POST'
            });

            if (res.ok) {
                setStatus('success');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');

                return;
            }

            setStatus(res.status === HTTP_STATUS_UNAUTHORIZED ? 'wrongCurrent' : 'error');
        } catch {
            setStatus('error');
        }
    };

    return (
        <Wrapper data-cy="ChangePasswordSection">
            <Form data-cy="ChangePasswordSection-form" onSubmit={handleSubmit}>
                <PasswordInput
                    autoComplete="current-password"
                    placeholder={t('changePassword.currentPlaceholder')}
                    testId="ChangePasswordSection-current"
                    value={currentPassword}
                    onChange={event => setCurrentPassword(event.target.value)}
                />
                <PasswordInput
                    autoComplete="new-password"
                    placeholder={t('changePassword.newPlaceholder')}
                    testId="ChangePasswordSection-new"
                    value={newPassword}
                    onChange={event => setNewPassword(event.target.value)}
                />
                <PasswordInput
                    autoComplete="new-password"
                    placeholder={t('changePassword.confirmPlaceholder')}
                    testId="ChangePasswordSection-confirm"
                    value={confirmPassword}
                    onChange={event => setConfirmPassword(event.target.value)}
                />
                {status === 'tooShort' && (
                    <ErrorMessage data-cy="ChangePasswordSection-tooShort">
                        {t('changePassword.tooShort')}
                    </ErrorMessage>
                )}
                {status === 'mismatch' && (
                    <ErrorMessage data-cy="ChangePasswordSection-mismatch">
                        {t('changePassword.mismatch')}
                    </ErrorMessage>
                )}
                {status === 'wrongCurrent' && (
                    <ErrorMessage data-cy="ChangePasswordSection-wrongCurrent">
                        {t('changePassword.wrongCurrent')}
                    </ErrorMessage>
                )}
                {status === 'error' && (
                    <ErrorMessage data-cy="ChangePasswordSection-error">
                        {t('changePassword.error')}
                    </ErrorMessage>
                )}
                {status === 'success' && (
                    <SuccessMessage data-cy="ChangePasswordSection-success">
                        {t('changePassword.success')}
                    </SuccessMessage>
                )}
                <Button
                    data-cy="ChangePasswordSection-submit"
                    disabled={status === 'submitting'}
                    size="small"
                    type="submit"
                    variant="primary"
                >
                    {status === 'submitting' ? t('changePassword.submitting') : t('changePassword.submit')}
                </Button>
            </Form>
        </Wrapper>
    );
};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

// const SubmitButton = styled.button`
//     background: ${({theme}) => theme.colors.primaryDefault};
//     border: none;
//     border-radius: 8px;
//     color: ${({theme}) => theme.colors.white};
//     cursor: pointer;
//     font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
//     font-size: 1.6rem;
//     font-weight: 700;
//     padding: ${spacing(3)} ${spacing(4)};
//     transition: opacity 0.15s ease;

//     &:disabled {
//         cursor: not-allowed;
//         opacity: 0.6;
//     }
// `;

const ErrorMessage = styled.p`
    color: ${({theme}) => theme.colors.accentDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
`;

const SuccessMessage = styled.p`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
`;